import { Badge, Checkbox } from "@xola/ui-kit";
import clsx from "clsx";
import React, { useEffect, useMemo, useState } from "react";
import { Schedule } from "types";
import { getScheduleSummary } from "../../helpers/scheduleSummaryHelper";
import { useSearch } from "../../hooks/useSearch";
import { SelectionState, useSelection } from "../../hooks/useSelection";
import { NoTranslate } from "./NoTranslate";
import { SearchInput } from "./SearchInput";

interface SavedSchedule extends Schedule {
    id: ID;
}

interface ScheduleSelectorProps {
    value: SelectionState;
    onChange: (value: SelectionState) => void;
    schedules: Schedule[];
    classNames?: { list?: string };
    enableSelectAll?: boolean;
}

const defaultValue: SelectionState = {
    all: true,
    selected: [],
};

export const ScheduleSelector = ({
    value = defaultValue,
    onChange,
    schedules,
    classNames,
    enableSelectAll = true,
}: ScheduleSelectorProps) => {
    const [searchTerm, setSearchTerm] = useState("");

    const filteredSchedules = useSearch(
        schedules,
        (schedule: Schedule) => [schedule.name],
        searchTerm,
    ) as SavedSchedule[];

    const isFiltered = searchTerm.length > 0;
    const isSubset = enableSelectAll ? isFiltered : true;
    const defaultSelection = useMemo(() => ({ all: value.all, selected: value.selected }), [value]);
    const selection = useSelection(filteredSchedules, isSubset, defaultSelection);

    useEffect(() => {
        onChange({ all: selection.state.all, selected: selection.state.selected });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.state]);

    const handleSearchChange = (searchResult: string) => {
        setSearchTerm(searchResult.trim());
    };

    return (
        <div className="ui-product-selector overflow-hidden rounded-lg border border-gray-lighter">
            <div className="space-y-3">
                <div className="px-4">
                    <SearchInput className="w-full" onChange={handleSearchChange} />
                </div>
                {enableSelectAll ? (
                    <div className="flex items-center justify-between bg-gray-lighter py-2 px-3">
                        <Checkbox
                            className="font-bold"
                            checked={selection.isSelectedAll}
                            label="Select All"
                            onChange={() => selection.selectAll()}
                        />
                        <Badge className="text-sm text-gray-darker" size="small">
                            <var data-var="selectionCount">{selection.count}</var> Selected
                        </Badge>
                    </div>
                ) : null}
                <div
                    className={clsx(
                        "h-60 divide-y divide-gray-light overflow-y-auto overflow-x-hidden rounded border border-gray-light",
                        classNames?.list,
                    )}
                >
                    {filteredSchedules.map((schedule) => (
                        <Checkbox
                            key={schedule.id}
                            checked={selection.isSelected(schedule)}
                            name={schedule.id}
                            label={
                                <NoTranslate if={!!schedule.name} className="block max-w-64 truncate text-black">
                                    {schedule.name?.trim() || getScheduleSummary(schedule)}
                                </NoTranslate>
                            }
                            className={clsx("p-3", selection.isSelected(schedule) ? "bg-primary-lighter" : "")}
                            classNames={{ checkbox: "!border !border-gray" }}
                            onChange={() => selection.select(schedule)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};
